import React, { useState } from 'react';
import { Check } from 'lucide-react';
import RegistrationModal from './RegistrationModal';

const ServicePricing = ({ showPrices = true, title, subtitle }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const plans = [
    {
      name: 'Basic',
      price: '$45',
      priceNote: '+ Federal Filing Fee',
      description: 'Essential trademark protection for startups',
      features: [
        'Case Review',
        'Basic Trademark Search',
        'Trademark Monitoring Services (1 Month)',
        'Trademark Secured\u00ae',
        'Complete Paperwork'
      ],
      popular: false
    },
    {
      name: 'Deluxe',
      price: '$149',
      priceNote: '+ Federal Filing Fee',
      description: 'Enhanced protection with expert review',
      features: [
        'Case Review by a TM Expert',
        'Basic Trademark Search',
        'Trademark Monitoring Services (1 Month)',
        'Trademark Secured\u00ae',
        'Refusal Risk Meter\u00ae',
        'Complete Paperwork'
      ],
      popular: true
    },
    {
      name: 'Premium',
      price: '$399',
      priceNote: '+ Federal Filing Fee',
      description: 'Comprehensive protection with priority service',
      features: [
        'Case Review & Filing by Paralegal Officer',
        'Expedited Application Processing within 24 Hours',
        'Filing along with Complete Paperwork',
        'Trademark Monitoring Services (3 Months)',
        'Trademark Secured\u00ae',
        'Refusal Risk Meter\u00ae',
        'Litigant Scan',
        'Comprehensive Trademark Search Report (50 States + social media/domains)',
        'Cease & Desist Letter',
        'Dedicated Account Manager'
      ],
      popular: false
    },
    {
      name: 'Signature',
      price: '$595',
      priceNote: 'Starting From',
      description: 'Premium service with U.S. Registered IP Attorney',
      features: [
        'Case Review & Filing by U.S Registered IP Attorney',
        'Assigned Case Filing Officer',
        'Private Registration',
        'Federal Search & Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
        'Cease & Desist Letter',
        'Same Day Filing',
        'Trademark Monitoring (3 Months)',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager',
        '24/7 Secure Online Access',
        '*(Govt. filing fee not included)'
      ],
      popular: false
    }
  ];

  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            {showPrices ? 'Pricing Plans' : 'Our Packages'}
          </div>
          <h2 className="text-4xl font-bold text-black mb-4">
            {title || (showPrices ? 'Smart, Affordable Trademark Protection' : 'Professional Service Packages')}
          </h2>
          {subtitle && <p className="text-lg text-gray-600">{subtitle}</p>}
        </div>

        {showPrices ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <div
                key={index}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                className={`bg-white rounded-2xl p-6 border-2 ${plan.popular ? 'border-[#FF035B] shadow-2xl transform scale-105' : 'border-gray-200'} flex flex-col group transition-all duration-300 hover:border-[#FF035B] hover:shadow-[0_10px_40px_rgba(255,3,91,0.3)] hover:-translate-y-2`}
              >
                {plan.popular && (
                  <div className="bg-[#FF035B] text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4 text-center">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2 transition-colors duration-300 group-hover:text-[#FF035B]">{plan.name}</h3>
                <p className="text-gray-600 mb-4 text-sm min-h-[40px]">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-black transition-colors duration-300 group-hover:text-[#FF035B]">{plan.price}</span>
                  <p className="text-sm text-gray-600 mt-1">{plan.priceNote}</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  data-testid={`pricing-btn-${plan.name.toLowerCase()}`}
                  className={`w-full py-3 text-sm font-semibold mb-6 rounded-lg transition-all duration-300 ${plan.popular ? 'bg-[#FF035B] hover:bg-[#2F1A8E] text-white' : 'bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white transform hover:scale-105'}`}
                >
                  Register My Trademark
                </button>
                <ul className="space-y-3 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#FF035B] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm leading-tight">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-10 border-2 border-gray-200 text-center shadow-lg hover:border-[#FF035B] hover:shadow-[0_10px_40px_rgba(255,3,91,0.3)] transition-all duration-300 group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF035B]/10 to-[#2F1A8E]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-[#FF035B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3 group-hover:text-[#FF035B] transition-colors duration-300">Customized Pricing for Your Needs</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Every case is unique. Get a personalized quote from our experts based on your specific requirements and scope.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setIsModalOpen(true)}
                  data-testid="get-a-quote-btn"
                  className="bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  Get a Quote
                </button>
                <button
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="border-2 border-[#FF035B] text-[#FF035B] hover:bg-[#FF035B] hover:text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300"
                >
                  Speak with an Expert
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-4">No credit card required &bull; Free initial consultation</p>
            </div>
          </div>
        )}
      </div>

      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default ServicePricing;
